// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse } from "react-bootstrap";
import QRCode from "react-qr-code";
import { Base64 } from "js-base64";

import { isSet } from "shared/utils/utils";
import { IAction } from "actions";
import { IState } from "states/states";
import { IOcppCharger, IRfid } from "states/ocppstates";

import FormattedDate from "./FormattedDate";
import FormattedTime from "./FormattedTime";

import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

interface IRfidDataProps {
  rfid: IRfid | undefined;
  ocppChargers: IOcppCharger[];
  isExtendedDataLoaded: boolean;
}

const mapStateToProps = (state: IState) => {
  const rfid = state.ocpp.rfid.find((curItem) => curItem.id === state.router.rfid);
  const props: IRfidDataProps = {
    rfid,
    ocppChargers: state.ocpp.charger,
    isExtendedDataLoaded: isSet(state.scheduler.ocppRfidListExt.lastData),
  };
  return props;
};

interface IRfidDataDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IRfidDataDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  msgUsedEnergy: {
    id: "used energy and costs exclude ongoing charging sessions",
  },
});

interface IRfidDataState {
  showLoginQr: boolean;
}

class RfidDataComponent extends React.Component<
  IRfidDataProps & IRfidDataDispatch,
  IRfidDataState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  constructor(props: IRfidDataProps & IRfidDataDispatch) {
    super(props);
    this.state = {
      showLoginQr: false,
    };
  }
  public render() {
    if (!this.props.rfid) {
      return <></>;
    }
    const rfid = this.props.rfid;
    const chargers = (rfid.chargers ?? [])
      .map((curItem) =>
        this.props.ocppChargers.find((curCharger) => curCharger.fk_charger === curItem),
      )
      .map((curItem) => curItem?.name ?? curItem?.chargepoint);

    return (
      <div id="rfiddata">
        <table className="datatable fullwidthwhite">
          <tbody>
            <tr>
              <th>
                <FormattedMessage id="Card number" />
              </th>
              <td>{this.props.rfid?.rfid}</td>
            </tr>
            {this.props.isExtendedDataLoaded && (
              <tr>
                <th>
                  <FormattedMessage id="Chargers" />
                </th>
                <td>
                  {this.props.rfid?.chargerRestricted ?
                    chargers.join(", ")
                  : <FormattedMessage id="all chargers" />}
                </td>
              </tr>
            )}
            {isSet(rfid.limitStart) && (
              <tr>
                <th>
                  <FormattedMessage id="Valid from" />
                </th>
                <td>
                  <FormattedDate valueMs={rfid.limitStart * 1000} />
                </td>
              </tr>
            )}
            {isSet(rfid.limitEnd) && (
              <tr>
                <th>
                  <FormattedMessage id="Valid until" />
                </th>
                <td>
                  <FormattedDate valueMs={rfid.limitEnd * 1000} />
                </td>
              </tr>
            )}
            {(isSet(rfid.limitEnergy) || isSet(rfid.limitCost)) && (
              <tr>
                <th
                  title={
                    isSet(rfid.usedEnergy) || isSet(rfid.usedCost) ?
                      this.context.formatMessage(messages.msgUsedEnergy)
                    : undefined
                  }
                >
                  <FormattedMessage id="Energy/Cost limit" />
                </th>
                <td>
                  {isSet(rfid.limitEnergy) &&
                    `${isSet(rfid.usedEnergy) ? `${Math.round(rfid.usedEnergy)} / ` : ""}${
                      rfid.limitEnergy
                    } kWh`}
                  {isSet(rfid.limitEnergy) && isSet(rfid.limitCost) && ", "}
                  {isSet(rfid.limitCost) &&
                    `${isSet(rfid.usedCost) ? `${Math.round(rfid.usedCost)} / ` : ""}${
                      rfid.limitCost
                    } EUR`}
                </td>
              </tr>
            )}
            {this.props.isExtendedDataLoaded && (
              <tr>
                <th>
                  <FormattedMessage id="Last Usage" />
                </th>
                <td>
                  {isSet(rfid.lastUsage) ?
                    <>
                      <FormattedDate valueMs={rfid.lastUsage * 1000} />{" "}
                      <FormattedTime valueMs={rfid.lastUsage * 1000} />
                    </>
                  : <FormattedMessage id="Never" />}
                </td>
              </tr>
            )}
            {this.renderLoginQr()}
          </tbody>
        </table>
      </div>
    );
  }

  private renderLoginQr() {
    if (!this.props.rfid?.rfid || !this.props.rfid.accessRights?.isLoginAllowed) {
      return null;
    }

    const authObj = {
      username: this.props.rfid.rfid,
      password: "",
    };
    const loginBase64 = Base64.encode(JSON.stringify(authObj));
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const loginUrl = `${baseUrl}/rfid?login=${loginBase64}`;

    return (
      <>
        <tr>
          <th style={{ verticalAlign: "top" }}>
            <FormattedMessage id="Auto-Login" />
          </th>
          <td>
            <span
              className="link"
              style={{ marginLeft: -3 }}
              onClick={() => this.setState({ showLoginQr: !this.state.showLoginQr })}
            >
              <Button variant="link" className="expand-button p-0">
                {this.state.showLoginQr ?
                  <IconExpandMore />
                : <IconExpandLess />}
              </Button>
              <FormattedMessage id="show auto-login URL" />
            </span>
          </td>
        </tr>
        {this.state.showLoginQr && (
          <tr>
            <td colSpan={2}>
              <Collapse in={this.state.showLoginQr}>
                <div className="mt-1">
                  <div className="mb-1" style={{ fontSize: "smaller" }}>
                    <FormattedMessage id="For auto-login, use the following link or scan the QR-Code:" />
                  </div>
                  <div>
                    <a
                      href={loginUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ wordBreak: "break-all" }}
                    >
                      {loginUrl}
                    </a>
                  </div>
                  <div style={{ background: "white", padding: "10px", display: "inline-block" }}>
                    <QRCode value={loginUrl} size={100} level="L" />
                  </div>
                </div>
              </Collapse>
            </td>
          </tr>
        )}
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RfidDataComponent);
